import {
  CancellationFee,
  CancellationFeeSchoolAssignment,
} from 'app/shared/model/CancellationFee';
import { Centre, School } from 'app/shared/model';
import {
  isExcludedCentreOrSchoolName,
  isExcludedCountry,
} from 'app/shared/locationExclusions';

export function normalizeCentreKey(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const match = /^CN(\d+)$/i.exec(trimmed);
  return match ? match[1] : trimmed;
}

export function normalizeSchoolKey(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const match = /^(?:School-)?(\d+)$/i.exec(trimmed);
  return match ? match[1] : trimmed;
}

export function centreKeysMatch(a: string | number, b: string | number): boolean {
  const left = normalizeCentreKey(a);
  const right = normalizeCentreKey(b);
  return left != null && right != null && left === right;
}

export function schoolKeysMatch(a: string | number, b: string | number): boolean {
  const left = normalizeSchoolKey(a);
  const right = normalizeSchoolKey(b);
  return left != null && right != null && left === right;
}

export type CancellationFeeScopeFilter = {
  specificSchool: boolean;
  centreId?: string | number | null;
  schoolId?: string | number | null;
};

export function hasActiveScopeFilter(filter: CancellationFeeScopeFilter): boolean {
  return filter.specificSchool
    ? filter.schoolId != null && filter.schoolId !== ''
    : filter.centreId != null && filter.centreId !== '';
}

function feeMatchesCentre(fee: CancellationFee, centreId: string | number): boolean {
  if ((fee.centreIds ?? []).some((id) => centreKeysMatch(id, centreId))) {
    return true;
  }
  return (fee.schools ?? []).some((school) => centreKeysMatch(school.centreId, centreId));
}

function feeMatchesSchool(fee: CancellationFee, schoolId: string | number): boolean {
  if ((fee.schoolIds ?? []).some((id) => schoolKeysMatch(id, schoolId))) {
    return true;
  }
  return (fee.schools ?? []).some((school) => schoolKeysMatch(school.schoolId, schoolId));
}

export function filterCancellationFeesByScope(
  fees: CancellationFee[],
  filter: CancellationFeeScopeFilter
): CancellationFee[] {
  if (!hasActiveScopeFilter(filter)) return fees;

  if (filter.specificSchool && filter.schoolId != null) {
    return fees.filter((fee) => feeMatchesSchool(fee, filter.schoolId!));
  }

  if (!filter.specificSchool && filter.centreId != null) {
    return fees.filter((fee) => feeMatchesCentre(fee, filter.centreId!));
  }

  return fees;
}

function resolveCentreLabel(
  centreId: string,
  centres: Centre[],
  fees: CancellationFee[]
): string {
  const centre = centres.find((c) => centreKeysMatch(c.id, centreId));
  if (centre?.name?.trim()) return centre.name.trim();

  for (const fee of fees) {
    for (const school of fee.schools ?? []) {
      if (centreKeysMatch(school.centreId, centreId) && school.centreName?.trim()) {
        return school.centreName.trim();
      }
    }
  }

  return centreId;
}

function resolveSchoolLabel(
  schoolId: string,
  schools: School[],
  fees: CancellationFee[]
): string {
  const school = schools.find((s) => schoolKeysMatch(s.id ?? '', schoolId));
  if (school?.name?.trim()) return school.name.trim();

  for (const fee of fees) {
    for (const assignment of fee.schools ?? []) {
      if (schoolKeysMatch(assignment.schoolId, schoolId) && assignment.schoolName?.trim()) {
        return assignment.schoolName.trim();
      }
    }
  }

  return schoolId;
}

function collectUsedCentreIds(fees: CancellationFee[]): Set<string> {
  const ids = new Set<string>();
  for (const fee of fees) {
    for (const id of fee.centreIds ?? []) {
      const key = normalizeCentreKey(id);
      if (key) ids.add(key);
    }
    for (const school of fee.schools ?? []) {
      const key = normalizeCentreKey(school.centreId);
      if (key) ids.add(key);
    }
  }
  return ids;
}

function collectUsedSchoolIds(fees: CancellationFee[]): Set<string> {
  const ids = new Set<string>();
  for (const fee of fees) {
    for (const id of fee.schoolIds ?? []) {
      const key = normalizeSchoolKey(id);
      if (key) ids.add(key);
    }
    for (const school of fee.schools ?? []) {
      const key = normalizeSchoolKey(school.schoolId);
      if (key) ids.add(key);
    }
  }
  return ids;
}

export function buildCentreFilterOptionsFromFees(
  fees: CancellationFee[],
  centres: Centre[]
): { value: string | number; label: string }[] {
  const usedIds = collectUsedCentreIds(fees);
  const assignableCentres = centres.filter((centre) => isCentreActive(centre.id, centres));
  const rows: { value: string | number; label: string }[] = [];

  for (const centre of assignableCentres) {
    const key = normalizeCentreKey(centre.id);
    if (!key || !usedIds.has(key)) continue;
    rows.push({ value: centre.id, label: centre.name.trim() });
  }

  for (const id of usedIds) {
    if (rows.some((row) => centreKeysMatch(row.value, id))) continue;
    if (!isCentreActive(id, centres)) continue;
    const label = resolveCentreLabel(id, centres, fees);
    if (!label || isExcludedCentreOrSchoolName(label)) continue;
    rows.push({ value: id, label });
  }

  rows.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return rows;
}

export function isCentreActive(centreId: string | number, centres: Centre[]): boolean {
  const centre = centres.find((c) => centreKeysMatch(c.id, centreId));
  if (!centre) return true;
  if (centre.active === false) return false;
  if (isExcludedCentreOrSchoolName(centre.name)) return false;
  if (isExcludedCountry(centre.countryName, centre.countryCode)) return false;
  return true;
}

export function isSchoolActive(
  schoolId: string | number,
  schools: School[],
  centres: Centre[]
): boolean {
  const school = schools.find((s) => schoolKeysMatch(s.id ?? '', schoolId));
  if (!school) return true;
  if (school.isActive === false) return false;
  if (isExcludedCentreOrSchoolName(school.name)) return false;
  if (school.centreId != null && !isCentreActive(school.centreId, centres)) return false;
  return true;
}

function isFeeSchoolAssignmentActive(
  assignment: CancellationFeeSchoolAssignment,
  schools: School[],
  centres: Centre[]
): boolean {
  if (isExcludedCentreOrSchoolName(assignment.schoolName)) return false;
  if (isExcludedCentreOrSchoolName(assignment.centreName)) return false;
  if (assignment.centreId != null && !isCentreActive(assignment.centreId, centres)) return false;
  return isSchoolActive(assignment.schoolId, schools, centres);
}

export function getActiveFeeSchoolAssignments(
  fee: CancellationFee,
  schools: School[],
  centres: Centre[]
): CancellationFeeSchoolAssignment[] {
  return (fee.schools ?? []).filter((assignment) =>
    isFeeSchoolAssignmentActive(assignment, schools, centres)
  );
}

export function getActiveFeeCentreIds(
  fee: CancellationFee,
  centres: Centre[]
): (string | number)[] {
  return (fee.centreIds ?? []).filter((id) => isCentreActive(id, centres));
}

export function getActiveFeeAssignmentSummary(
  fee: CancellationFee,
  centres: Centre[],
  schools: School[]
): { schoolNames: string[]; centreCount: number } {
  const activeSchools = getActiveFeeSchoolAssignments(fee, schools, centres);
  const activeCentreIds = getActiveFeeCentreIds(fee, centres);
  return {
    schoolNames: activeSchools.map((s) => s.schoolName || '').filter(Boolean),
    centreCount: activeCentreIds.length,
  };
}

export function buildAssignedCentresSchoolsList(
  fee: CancellationFee,
  centres: Centre[],
  schools: School[]
): string[] {
  const { schoolNames, centreCount } = getActiveFeeAssignmentSummary(fee, centres, schools);
  const centreText = centreCount ? [`${centreCount} centre${centreCount === 1 ? '' : 's'}`] : [];
  return [...centreText, ...schoolNames];
}

export function buildSchoolFilterOptionsFromFees(
  fees: CancellationFee[],
  schools: School[],
  centres: Centre[]
): { value: string | number; label: string }[] {
  const usedIds = collectUsedSchoolIds(fees);
  const assignableSchools = schools.filter((school) => isSchoolActive(school.id ?? '', schools, centres));
  const rows: { value: string | number; label: string }[] = [];

  for (const school of assignableSchools) {
    const key = normalizeSchoolKey(school.id ?? '');
    if (!key || !usedIds.has(key)) continue;
    rows.push({ value: school.id ?? key, label: school.name.trim() });
  }

  for (const id of usedIds) {
    if (rows.some((row) => schoolKeysMatch(row.value, id))) continue;
    if (!isSchoolActive(id, schools, centres)) continue;
    const label = resolveSchoolLabel(id, schools, fees);
    if (!label || isExcludedCentreOrSchoolName(label)) continue;
    rows.push({ value: id, label });
  }

  rows.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return rows;
}